import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToastHandler } from "@/hooks/useToastHandler";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { settingsApi } from "@/api/settingsApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageTitleScript from "@/components/MetaScript";
import { timeZoneOptions } from "@/lib/options/timezoneOptions";
import { genderOptions } from "@/lib/options/genderOptions";
import { birthYearOptions } from "@/lib/options/birthYearOptions";
import { useEffectiveUser } from "@/hooks/useEffectiveUser";
import { Input } from "@/components/ui/input";
import { AxiosError } from "axios";
import { useValidationErrors } from "@/hooks/useValidationErrors";

// Provider Icons
function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path
        d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
        fill="#000"
      />
    </svg>
  );
}

export function SettingsProfilePage() {
  const [name, setName] = useState<string | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [birthYear, setBirthYear] = useState<number | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const {
    validationErrors,
    removeValidationError,
    clearAllValidationErrors,
    setValidationErrorsFromResponse,
  } = useValidationErrors();
  const { handleSuccess, handleError } = useToastHandler();
  const navigate = useNavigate();

  // ######### react hook
  const { user, isLoading, refetchUserInfo } = useEffectiveUser();

  useEffect(() => {
    if (!user) return;
    setName(user.name || null);
    setGender(user.gender || null);
    setBirthYear(user.birth_year || null);
    setTimezone(user.timezone_offset || null);
  }, [user]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Call API to update user profile
      await settingsApi.updateProfile({
        name: name as string | undefined,
        gender: gender as string | undefined,
        birth_year: birthYear as number | undefined,
        timezone_offset: timezone as string | undefined,
      });

      handleSuccess("Profile updated successfully", 1000);
      // Clear validation errors on success
      clearAllValidationErrors();
      refetchUserInfo();
    } catch (err) {
      if (err instanceof AxiosError && err.response?.data?.validation_errors) {
        // Store validation errors to display under inputs
        setValidationErrorsFromResponse(err.response.data.validation_errors);
        //return; // Don't show generic error toast for validation errors
      }
      handleError(err as unknown, 3000);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value === "null" ? null : value);
    // Clear validation errors when user starts typing
    removeValidationError("name");
  };

  const handleGenderChange = (value: string) => {
    setGender(value === "null" ? null : value);
    // Clear validation errors when user selects
    removeValidationError("gender");
  };

  const handleBirthYearChange = (value: string) => {
    setBirthYear(value === "null" ? null : Number(value));
    // Clear validation errors when user selects
    removeValidationError("birth_year");
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value === "null" ? null : value);
    // Clear validation errors when user selects
    removeValidationError("timezone_offset");
  };

  return (
    <div className="flex flex-col p-6">
      <PageTitleScript title="Profile" />
      <div className="flex items-center mb-6">
        <button onClick={() => navigate(-1)} className="mr-2">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-2xl font-bold text-tx-dark">Profile</h1>
      </div>

      {/* Profile Section */}
      <div className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-xs mb-6">
        <div className="relative">
          <Avatar className="h-20 w-20 border-2 border-gray-200">
            <AvatarImage
              src={user?.avatar_picture || undefined}
              alt={`${user?.name} avatar`}
              loading="lazy"
            />
            <AvatarFallback className="text-xl font-medium bg-gray-100 text-gray-600">
              N/A
            </AvatarFallback>
          </Avatar>
          {/* Provider Icon */}
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 border border-gray-200 shadow-sm">
            {user?.provider === "google" && <GoogleIcon />}
            {user?.provider === "apple" && <AppleIcon />}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">
            {user?.name || "User"}
          </h2>
          <p className="text-base text-gray-500">{user?.email}</p>
        </div>
      </div>

      <Card className="rounded-xl overflow-hidden shadow-md py-10 bg-white border border-bd-light">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bd-dark"></div>
          </div>
        ) : (
          <>
            <div className="px-8">
              {/* name input */}
              <div className="mb-6">
                <label
                  htmlFor="name-input"
                  className="block text-sm font-medium mb-2"
                >
                  Name
                </label>
                <Input
                  id="name-input"
                  type="text"
                  value={name || ""}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`w-full ${
                    validationErrors.name ? "border-red-500" : ""
                  }`}
                />
                {validationErrors.name && (
                  <div className="mt-1">
                    {validationErrors.name.map((error, index) => (
                      <p key={index} className="text-sm text-red-600">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              {/* Gender Selection */}
              <div className="mb-6">
                <label
                  htmlFor="gender-select"
                  className="block text-sm font-medium mb-2"
                >
                  Gender
                </label>
                <Select
                  value={gender === null ? "null" : gender}
                  onValueChange={handleGenderChange}
                >
                  <SelectTrigger
                    id="gender-select"
                    className={`w-full ${
                      validationErrors.gender ? "border-red-500" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {genderOptions.map((option) => (
                      <SelectItem
                        key={String(option.value)}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.gender && (
                  <div className="mt-1">
                    {validationErrors.gender.map((error, index) => (
                      <p key={index} className="text-sm text-red-600">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Birth Year Selection */}
              <div className="mb-6">
                <label
                  htmlFor="birth-year-select"
                  className="block text-sm font-medium mb-2"
                >
                  Birth Year
                </label>
                <Select
                  value={birthYear === null ? "null" : String(birthYear)}
                  onValueChange={handleBirthYearChange}
                >
                  <SelectTrigger
                    id="birth-year-select"
                    className={`w-full ${
                      validationErrors.birth_year ? "border-red-500" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select birth year" />
                  </SelectTrigger>
                  <SelectContent>
                    {birthYearOptions.map((option) => (
                      <SelectItem
                        key={String(option.value)}
                        value={String(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.birth_year && (
                  <div className="mt-1">
                    {validationErrors.birth_year.map((error, index) => (
                      <p key={index} className="text-sm text-red-600">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Timezone Selection */}
              <div className="mb-6">
                <label
                  htmlFor="timezone-select"
                  className="block text-sm font-medium mb-2"
                >
                  Timezone
                </label>
                <Select
                  value={timezone === null ? "null" : timezone}
                  onValueChange={handleTimezoneChange}
                >
                  <SelectTrigger
                    id="timezone-select"
                    className={`w-full ${
                      validationErrors.timezone_offset ? "border-red-500" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeZoneOptions.map((option) => (
                      <SelectItem
                        key={String(option.key)}
                        value={String(option.offset)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.timezone_offset && (
                  <div className="mt-1">
                    {validationErrors.timezone_offset.map((error, index) => (
                      <p key={index} className="text-sm text-red-600">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Button */}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
